import { enumerate } from "common/utils";

export const getFormData = (form) => {
  const data = new FormData(form);
  return Object.fromEntries(data.entries());
};
const parse = (value) =>
  value === "on"
    ? true
    : value === "off"
    ? false
    : parseAsNumber(value) || value;

const parseAsNumber = (value, delimiter = ".") => {
  const parsed = value.includes(delimiter)
    ? parseFloat(value)
    : parseInt(value);
  return isNaN(parsed) ? value : parsed;
};

const intoStructure = (settings, [[field, ...rest], value]) => ({
  ...settings,
  ...{
    [field]:
      rest.length === 0
        ? parse(value)
        : intoStructure(settings[field], [rest, value]),
  },
});

export const rankAndParseFields = (source, defaults) =>
  Object.entries(source)
    .map(([field, value]) => [
      field.replace(/\[bqn (\d+|all)\]/, "").split("."),
      value,
    ])
    .reduce(intoStructure, defaults);

export function mergeAndMetadata(objectA) {
  const bqnIds = Object.keys(objectA).filter((k) => k !== "bypass");
  const firstBqn = bqnIds[0];

  const metadata = {};

  function mergeValues(path, valuesByBqn) {
    const values = Object.values(valuesByBqn);

    if (typeof values[0] === "boolean") {
      const allSame = values.every((v) => v === values[0]);

      setMetadata(
        path,
        bqnIds.reduce((acc, id) => {
          acc[id] = valuesByBqn[id];
          return acc;
        }, {}),
      );

      return allSame ? values[0] : "both";
    }

    if (typeof values[0] === "number") {
      setMetadata(
        path,
        bqnIds.reduce((acc, id) => {
          acc[id] = valuesByBqn[id];
          return acc;
        }, {}),
      );

      return valuesByBqn[firstBqn];
    }

    if (typeof values[0] === "string" || values[0] === null) {
      return valuesByBqn[firstBqn];
    }

    if (Array.isArray(values[0])) {
      return valuesByBqn[firstBqn];
    }

    const result = {};
    const keys = Object.keys(values[0] || {});

    for (const key of keys) {
      const nextValues = {};
      for (const bqn of bqnIds) {
        nextValues[bqn] = valuesByBqn[bqn][key];
      }
      result[key] = mergeValues([...path, key], nextValues);
    }

    return result;
  }

  function setMetadata(path, value) {
    let ref = metadata;
    for (let i = 0; i < path.length - 1; i++) {
      if (!ref[path[i]]) ref[path[i]] = {};
      ref = ref[path[i]];
    }
    ref[path[path.length - 1]] = value;
  }

  const merge = {};
  const topKeys = Object.keys(objectA[firstBqn]).filter((k) => k !== "bypass");

  for (const key of topKeys) {
    const valuesByBqn = {};
    for (const bqn of bqnIds) {
      valuesByBqn[bqn] = objectA[bqn][key];
    }
    merge[key] = mergeValues([key], valuesByBqn);
  }

  const bypass = {};
  const bypassMeta = {};
  const bypassData = objectA.bypass;

  const bypassKeys = Object.keys(bypassData[firstBqn]);

  for (const key of bypassKeys) {
    const valuesByBqn = {};
    for (const bqn of bqnIds) {
      valuesByBqn[bqn] = bypassData[bqn][key];
    }

    const values = Object.values(valuesByBqn);

    if (typeof values[0] === "boolean") {
      const allSame = values.every((v) => v === values[0]);

      bypass[key] = allSame ? values[0] : "both";

      bypassMeta[key] = bqnIds.reduce((acc, id) => {
        acc[id] = valuesByBqn[id];
        return acc;
      }, {});
    } else if (key === "vlans") {
      const map = {};
      const union = new Map();
      const initValue = bqnIds.reduce((acc, id) => {
        acc[id] = false;
        return acc;
      }, {});

      for (const bqn of bqnIds) {
        for (const v of valuesByBqn[bqn]) {
          union.set(v.tag, v);

          if (!map[v.tag]) map[v.tag] = JSON.parse(JSON.stringify(initValue));
          map[v.tag][Number(bqn)] = true;
        }
      }

      bypass[key] = enumerate(1)(Array.from(union.values()));
      bypassMeta[key] = map;
    } else if (key === "ipRanges") {
      const map = {};
      const union = new Map();
      const initValue = bqnIds.reduce((acc, id) => {
        acc[id] = false;
        return acc;
      }, {});

      for (const bqn of bqnIds) {
        for (const ip of valuesByBqn[bqn]) {
          union.set(ip.address, ip);

          if (!map[ip.address])
            map[ip.address] = JSON.parse(JSON.stringify(initValue));
          map[ip.address][Number(bqn)] = true;
        }
      }

      bypass[key] = enumerate(1)(Array.from(union.values()));
      bypassMeta[key] = map;
    } else {
      bypass[key] = valuesByBqn[firstBqn];
    }
  }

  merge.bypass = bypass;
  metadata.bypass = bypassMeta;

  return { merge, metadata };
}
